import React, { useState, useEffect, useRef, useMemo } from 'react';
import ScrollHint from '../components/ScrollHint';
import { Link } from 'react-router-dom';
import {
  uploadEverWashReport,
  uploadCCTransactions, getUploads, deleteUpload, getRevenue, getRevenueHistograms,
  getWashPackageMix,
  getSalesTaxStatus, createSalesTaxTransfer, listSalesTaxTransfers,
  deleteSalesTaxTransfer, setTaxFilingFrequency,
  uploadUtilityBill, getUtilityBills, updateUtilityBill, deleteUtilityBill,
  downloadUtilityBill, downloadUtilityBillsBulk
} from '../services/plService';
import { updateSettings } from '../services/settingsService';
import { track } from '../services/analyticsService';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, Cell
} from 'recharts';
import { useLocationContext } from '../contexts/LocationContext';
import UnmappedSwipersModal from '../components/UnmappedSwipersModal';
import UnmappedSitesModal from '../components/UnmappedSitesModal';

const SalesTaxCalculator = () => {
  const { currentLocation } = useLocationContext();
  const taxRate = currentLocation?.tax_rate ?? 6;
  const [frequency, setFrequency] = useState(() => {
    return localStorage.getItem('salesTax_frequency') || 'quarterly';
  });
  const [showCredit, setShowCredit] = useState(() => {
    return localStorage.getItem('salesTax_showCredit') === 'true';
  });
  const [taxData, setTaxData] = useState(null);
  const [taxLoading, setTaxLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('');

  // Persist settings to localStorage
  useEffect(() => { localStorage.setItem('salesTax_frequency', frequency); }, [frequency]);
  useEffect(() => { localStorage.setItem('salesTax_showCredit', String(showCredit)); }, [showCredit]);

  const fmtDate = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const fmtDisplay = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const fmtDollar = (val) => {
    if (val == null) return '$0.00';
    return '$' + Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Generate available periods (current + past 4) based on frequency
  const periods = useMemo(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const result = [];

    const makePeriod = (start, end, label) => ({
      key: fmtDate(start),
      label,
      start,
      end,
      paymentDue: new Date(end.getFullYear(), end.getMonth() + 1, 20),
    });

    const count = 5; // current + 4 previous
    for (let i = 0; i < count; i++) {
      switch (frequency) {
        case 'monthly': {
          const m = month - i;
          const s = new Date(year, m, 1);
          const e = new Date(year, m + 1, 0);
          result.push(makePeriod(s, e, s.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })));
          break;
        }
        case 'quarterly': {
          const q = Math.floor(month / 3) - i;
          const s = new Date(year, q * 3, 1);
          const e = new Date(year, q * 3 + 3, 0);
          const qNum = Math.floor(s.getMonth() / 3) + 1;
          result.push(makePeriod(s, e, `Q${qNum} ${s.getFullYear()}`));
          break;
        }
        case 'biannual': {
          const h = (month < 6 ? 0 : 1) - i;
          const s = new Date(year, h * 6, 1);
          const e = new Date(year, h * 6 + 6, 0);
          const hNum = s.getMonth() < 6 ? 1 : 2;
          result.push(makePeriod(s, e, `H${hNum} ${s.getFullYear()}`));
          break;
        }
        case 'annual': {
          const y = year - i;
          result.push(makePeriod(new Date(y, 0, 1), new Date(y, 11, 31), String(y)));
          break;
        }
        default: {
          const qd = Math.floor(month / 3) - i;
          const s = new Date(year, qd * 3, 1);
          const e = new Date(year, qd * 3 + 3, 0);
          const qdNum = Math.floor(s.getMonth() / 3) + 1;
          result.push(makePeriod(s, e, `Q${qdNum} ${s.getFullYear()}`));
        }
      }
    }
    return result;
  }, [frequency]);

  // Reset to current period when frequency changes
  useEffect(() => {
    setSelectedPeriod(periods[0]?.key || '');
  }, [frequency, periods]);

  const currentPeriod = periods.find(p => p.key === selectedPeriod) || periods[0];
  const periodStart = currentPeriod?.start;
  const periodEnd = currentPeriod?.end;
  const paymentDue = currentPeriod?.paymentDue;
  const isPastPeriod = periodEnd && periodEnd < new Date();

  // Fetch revenue data for the selected tax period
  useEffect(() => {
    if (!periodStart || !periodEnd) return;
    const fetchTaxData = async () => {
      setTaxLoading(true);
      try {
        const endDate = isPastPeriod ? periodEnd : new Date();
        const data = await getRevenue({
          start_date: fmtDate(periodStart),
          end_date: fmtDate(endDate),
        });
        setTaxData(data);
      } catch (err) {
        console.error('Error fetching tax data:', err);
      } finally {
        setTaxLoading(false);
      }
    };
    fetchTaxData();
  }, [selectedPeriod]);

  // Calculations
  const rate = taxRate / 100;
  const totalRevenue = taxData
    ? (taxData.total_revenue ?? ((taxData.cc_revenue?.total || 0) + (taxData.cash_revenue?.total || 0)))
    : 0;
  const taxableSales = totalRevenue / (1 + rate);
  const grossTax = rate * taxableSales;
  const soapCost = taxData?.expenses?.soap || 0;
  const soapCredit = showCredit ? soapCost * rate : 0;
  const netTaxLiability = grossTax - soapCredit;

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900">Sales Tax Calculator</h2>
      </div>
      <div className="p-6 space-y-4">
        {/* Settings row */}
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-gray-700">Tax Rate:</span>
            <span className="text-sm text-gray-900">{taxRate}%</span>
            <span className="text-xs text-gray-400">(via Settings)</span>
          </div>
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">Filing Frequency:</label>
            <select
              value={frequency}
              onChange={(e) => setFrequency(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-1 text-sm bg-white"
            >
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="biannual">Biannual</option>
              <option value="annual">Annual</option>
            </select>
          </div>
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">Period:</label>
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-1 text-sm bg-white"
            >
              {periods.map(p => (
                <option key={p.key} value={p.key}>{p.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Period info */}
        <div className="text-sm text-gray-600">
          <p><span className="font-medium">Period:</span> {fmtDisplay(periodStart)} – {fmtDisplay(periodEnd)}</p>
          <p><span className="font-medium">Payment Due:</span> {fmtDisplay(paymentDue)}</p>
        </div>

        {/* Calculations */}
        {taxLoading ? (
          <p className="text-sm text-gray-500">Loading tax data...</p>
        ) : (
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-700">Gross Revenue ({fmtDisplay(periodStart)} – {isPastPeriod ? fmtDisplay(periodEnd) : 'today'})</span>
              <span className="font-medium text-gray-900">{fmtDollar(totalRevenue)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-700">Taxable Sales (Revenue / {(1 + rate).toFixed(2)})</span>
              <span className="font-medium text-gray-900">{fmtDollar(taxableSales)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-700">Sales Tax Liability ({taxRate}%)</span>
              <span className="font-medium text-gray-900">{fmtDollar(grossTax)}</span>
            </div>

            <div className="pt-2">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showCredit}
                  onChange={(e) => setShowCredit(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">Taxes Paid / Purchases Resold Credit</span>
              </label>
            </div>

            {showCredit && (
              <div className="pl-6 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Est. Soap Cost in Period</span>
                  <span className="text-gray-900">{fmtDollar(soapCost)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Credit (soap cost × {taxRate}%)</span>
                  <span className="text-green-700">−{fmtDollar(soapCredit)}</span>
                </div>
              </div>
            )}

            <div className="border-t border-gray-300 pt-2 mt-2">
              <div className="flex justify-between text-sm font-bold">
                <span className="text-gray-900">Net Tax Due</span>
                <span className="text-gray-900">{fmtDollar(netTaxLiability)}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const PLDashboard = () => {
  const { currentLocation, refreshLocations, locations } = useLocationContext();
  const [uploads, setUploads] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [unmappedSwipers, setUnmappedSwipers] = useState(null);
  const [unmappedSites, setUnmappedSites] = useState(null);
  // Keep the original file blob so we can re-upload it once the user
  // finishes mapping unmapped SiteIDs, without asking them to re-pick it.
  const [pendingUploadFile, setPendingUploadFile] = useState(null);
  const [showEverWashModal, setShowEverWashModal] = useState(false);
  const [everwashUploading, setEverWashUploading] = useState(false);
  const [everwashResult, setEverWashResult] = useState(null);
  const [everwashError, setEverWashError] = useState(null);
  const [unmappedEverWash, setUnmappedEverWash] = useState(null);
  const [pendingEverWashFile, setPendingEverWashFile] = useState(null);
  const everwashInputRef = useRef(null);
  const [showUtilityModal, setShowUtilityModal] = useState(false);
  const [utilityBills, setUtilityBills] = useState([]);
  const [utilityUploading, setUtilityUploading] = useState(false);
  const [utilityError, setUtilityError] = useState(null);
  const [utilityResult, setUtilityResult] = useState(null);
  const [utilityCategory, setUtilityCategory] = useState('gas');
  const [utilityManual, setUtilityManual] = useState({ amount: '', period_start: '', period_end: '', provider: '' });
  const [utilityParsed, setUtilityParsed] = useState(null);
  const [utilityPendingFile, setUtilityPendingFile] = useState(null);
  const utilityFileRef = useRef(null);
  const [editingBill, setEditingBill] = useState(null);
  const [editForm, setEditForm] = useState({ amount: '', period_start: '', period_end: '' });
  const [selectedBillIds, setSelectedBillIds] = useState(new Set());
  const [bulkDownloading, setBulkDownloading] = useState(false);

  // Histogram state
  const [histograms, setHistograms] = useState(null);
  const [histBreakdown, setHistBreakdown] = useState('none'); // 'none', 'payment', 'unit'
  const [hourStart, setHourStart] = useState(currentLocation?.operating_hours_start ?? 7);
  const [hourEnd, setHourEnd] = useState(currentLocation?.operating_hours_end ?? 19);

  // Wash package mix state
  const [chartGranularity, setChartGranularity] = useState('daily'); // 'daily', 'trailing', 'weekly', 'monthly', 'yearly'
  const [trailingDays, setTrailingDays] = useState(30);
  const [chartRevenue, setChartRevenue] = useState(null); // extended-range revenue for trailing sums
  const [washMix, setWashMix] = useState(null);
  const [washMixView, setWashMixView] = useState('overall'); // 'overall', 'hour', 'dow'

  // Date range — default last 30 days
  const today = new Date().toISOString().split('T')[0];
  const thirtyDaysAgo = new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0];
  const [startDate, setStartDate] = useState(thirtyDaysAgo);
  const [endDate, setEndDate] = useState(today);
  const [datePreset, setDatePreset] = useState('30');

  useEffect(() => {
    fetchData();
  }, [currentLocation?.id]);

  useEffect(() => {
    setHourStart(currentLocation?.operating_hours_start ?? 7);
    setHourEnd(currentLocation?.operating_hours_end ?? 19);
  }, [currentLocation?.id]);

  useEffect(() => {
    fetchRevenue();
    fetchHistograms();
    fetchWashMix();
  }, [startDate, endDate, currentLocation?.id]);

  useEffect(() => {
    if (chartGranularity === 'trailing') {
      fetchChartRevenue();
    } else {
      setChartRevenue(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartGranularity, trailingDays, startDate, endDate, currentLocation?.id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [uploadsData, billsData] = await Promise.all([getUploads(), getUtilityBills()]);
      setUploads(uploadsData.uploads || []);
      setUtilityBills(billsData.bills || []);
      await fetchRevenue();
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchRevenue = async () => {
    try {
      const data = await getRevenue({ start_date: startDate, end_date: endDate });
      setRevenue(data);
    } catch (err) {
      console.error('Error fetching revenue:', err);
    }
  };

  const fetchChartRevenue = async () => {
    try {
      const extended = new Date(startDate + 'T12:00:00');
      extended.setDate(extended.getDate() - (trailingDays - 1));
      const extendedStart = extended.toISOString().slice(0, 10);
      const data = await getRevenue({ start_date: extendedStart, end_date: endDate });
      setChartRevenue(data);
    } catch (err) {
      console.error('Error fetching extended chart revenue:', err);
    }
  };

  const fetchHistograms = async () => {
    try {
      const data = await getRevenueHistograms({ start_date: startDate, end_date: endDate });
      setHistograms(data);
    } catch (err) {
      console.error('Error fetching histograms:', err);
    }
  };

  const fetchWashMix = async () => {
    try {
      const data = await getWashPackageMix({ start_date: startDate, end_date: endDate });
      setWashMix(data);
    } catch (err) {
      console.error('Error fetching wash mix:', err);
    }
  };

  const handleFileUpload = async (file) => {
    if (!file) return;
    try {
      setUploading(true);
      setUploadResult(null);
      setError(null);
      const result = await uploadCCTransactions(file);
      track('cc_csv_upload');
      setUploadResult(result);
      // Refresh data
      const uploadsData = await getUploads();
      setUploads(uploadsData.uploads || []);
      await fetchRevenue();
      // Multi-site CSV with unrecognized SiteIDs: prompt the user to map
      // each one to a Copilot location, then re-ingest the same file.
      // Runs BEFORE the swipers modal so newly-imported rows go through
      // that flow next.
      if (result?.unmapped_sites?.length > 0) {
        setPendingUploadFile(file);
        setUnmappedSites(result.unmapped_sites);
        return; // don't open the swiper modal yet
      }
      // Otherwise, if any SwiperIDs from this file couldn't be routed to
      // a specific unit, open the swiper mapping modal.
      if (result?.unmapped_swipers?.length > 0) {
        setUnmappedSwipers(result.unmapped_swipers);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleEverWashUpload = async (file) => {
    if (!file) return;
    try {
      setEverWashUploading(true);
      setEverWashResult(null);
      setEverWashError(null);
      const result = await uploadEverWashReport(file);
      track('everwash_csv_upload');
      setEverWashResult(result);
      await fetchRevenue();
      // Report covering washes we haven't mapped yet: collect the routing
      // choices, then re-run the same file so the skipped rows land.
      if (result?.unmapped_sites?.length > 0) {
        setPendingEverWashFile(file);
        setUnmappedEverWash(result.unmapped_sites);
      }
    } catch (err) {
      setEverWashError(err.response?.data?.error || 'Upload failed');
    } finally {
      setEverWashUploading(false);
      if (everwashInputRef.current) everwashInputRef.current.value = '';
    }
  };

  const handleDeleteUpload = async (id) => {
    if (!window.confirm('Delete this upload and all its transactions?')) return;
    try {
      await deleteUpload(id);
      const uploadsData = await getUploads();
      setUploads(uploadsData.uploads || []);
      await fetchRevenue();
    } catch (err) {
      setError('Failed to delete upload');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileUpload(file);
  };

  const BILL_CATEGORIES = [
    { value: 'gas', label: 'Gas' },
    { value: 'water_sewer', label: 'Water/Sewer' },
    { value: 'trash', label: 'Trash' },
    { value: 'electric', label: 'Electric' },
    { value: 'cc_processing', label: 'CC Processing' },
    { value: 'internet', label: 'Internet' },
    { value: 'insurance', label: 'Insurance' },
    { value: 'everwash_fees', label: 'EverWash Fees' },
  ];

  const handleUtilityUpload = async (file) => {
    if (!file) return;
    try {
      setUtilityUploading(true);
      setUtilityError(null);
      setUtilityResult(null);
      setUtilityParsed(null);

      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', utilityCategory);
      // Include manual fields if provided
      if (utilityManual.amount) formData.append('amount', utilityManual.amount);
      if (utilityManual.period_start) formData.append('period_start', utilityManual.period_start);
      if (utilityManual.period_end) formData.append('period_end', utilityManual.period_end);
      if (utilityManual.provider) formData.append('provider', utilityManual.provider);

      const result = await uploadUtilityBill(formData);
      track('bill_upload');
      setUtilityResult(result);
      setUtilityManual({ amount: '', period_start: '', period_end: '', provider: '' });
      setUtilityPendingFile(null);
      const billsData = await getUtilityBills();
      setUtilityBills(billsData.bills || []);
      await fetchRevenue();
    } catch (err) {
      if (err.response?.status === 422 && err.response?.data?.parsed) {
        setUtilityParsed(err.response.data.parsed);
        setUtilityPendingFile(file);  // keep file so manual submit can re-attach it
        setUtilityError('Could not fully parse bill. Please fill in the missing fields below and re-upload, or enter manually.');
        // Pre-fill manual fields with what was parsed
        const p = err.response.data.parsed;
        setUtilityManual(prev => ({
          ...prev,
          amount: p.amount ? String(p.amount) : prev.amount,
          period_start: p.period_start || prev.period_start,
          period_end: p.period_end || prev.period_end,
          provider: p.provider || prev.provider,
        }));
      } else {
        setUtilityError(err.response?.data?.error || 'Upload failed');
      }
    } finally {
      setUtilityUploading(false);
      if (utilityFileRef.current) utilityFileRef.current.value = '';
    }
  };

  const handleUtilityManualSubmit = async () => {
    if (!utilityManual.amount || !utilityManual.period_start || !utilityManual.period_end) {
      setUtilityError('Amount, start date, and end date are required.');
      return;
    }
    try {
      setUtilityUploading(true);
      setUtilityError(null);
      setUtilityResult(null);

      const formData = new FormData();
      if (utilityPendingFile) formData.append('file', utilityPendingFile);
      formData.append('category', utilityCategory);
      formData.append('amount', utilityManual.amount);
      formData.append('period_start', utilityManual.period_start);
      formData.append('period_end', utilityManual.period_end);
      if (utilityManual.provider) formData.append('provider', utilityManual.provider);

      const result = await uploadUtilityBill(formData);
      track('bill_upload');
      setUtilityResult(result);
      setUtilityManual({ amount: '', period_start: '', period_end: '', provider: '' });
      setUtilityPendingFile(null);
      const billsData = await getUtilityBills();
      setUtilityBills(billsData.bills || []);
      await fetchRevenue();
    } catch (err) {
      setUtilityError(err.response?.data?.error || 'Failed to save bill');
    } finally {
      setUtilityUploading(false);
    }
  };

  const handleDeleteBill = async (id) => {
    if (!window.confirm('Delete this utility bill?')) return;
    try {
      await deleteUtilityBill(id);
      const billsData = await getUtilityBills();
      setUtilityBills(billsData.bills || []);
      await fetchRevenue();
    } catch (err) {
      setUtilityError('Failed to delete bill');
    }
  };

  const handleEditBill = (bill) => {
    setEditingBill(bill.id);
    setEditForm({
      amount: String(bill.amount),
      period_start: bill.period_start,
      period_end: bill.period_end,
    });
  };

  const handleSaveEdit = async () => {
    try {
      await updateUtilityBill(editingBill, {
        amount: parseFloat(editForm.amount),
        period_start: editForm.period_start,
        period_end: editForm.period_end,
      });
      setEditingBill(null);
      const billsData = await getUtilityBills();
      setUtilityBills(billsData.bills || []);
      await fetchRevenue();
    } catch (err) {
      setUtilityError('Failed to update bill');
    }
  };

  const categoryLabel = (cat) => {
    const found = BILL_CATEGORIES.find(c => c.value === cat);
    if (found) return found.label;
    // Fallback: replace underscores and title-case
    return cat.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const saveBlobAsFile = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadBill = async (bill) => {
    try {
      const res = await downloadUtilityBill(bill.id);
      const cat = (bill.category || 'bill').replace(/\s+/g, '_');
      const period = bill.period_end || 'unknown';
      saveBlobAsFile(res.data, `${cat}_${period}.pdf`);
    } catch (err) {
      setUtilityError('Failed to download bill');
    }
  };

  const toggleBillSelected = (id) => {
    setSelectedBillIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectableBills = utilityBills.filter((b) => b.has_file);
  const allSelectableSelected =
    selectableBills.length > 0 && selectableBills.every((b) => selectedBillIds.has(b.id));

  const toggleSelectAllBills = () => {
    if (allSelectableSelected) {
      setSelectedBillIds(new Set());
    } else {
      setSelectedBillIds(new Set(selectableBills.map((b) => b.id)));
    }
  };

  const handleDownloadSelected = async () => {
    const ids = Array.from(selectedBillIds);
    if (ids.length === 0) return;
    setBulkDownloading(true);
    try {
      const res = await downloadUtilityBillsBulk(ids);
      const today = new Date().toISOString().split('T')[0];
      saveBlobAsFile(res.data, `utility-bills-${today}.zip`);
    } catch (err) {
      setUtilityError('Failed to download selected bills');
    } finally {
      setBulkDownloading(false);
    }
  };

  const fmt = (val) => {
    if (val == null) return '$0.00';
    return '$' + Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const handleExport = () => {
    if (!revenue) return;
    const rows = (revenue.revenue_over_time || []).map(row => ({
      Date: row.date,
      'CC Revenue': row.cc_revenue,
      'Cash Revenue': row.cash_revenue,
      'Total Revenue': row.total_revenue,
      'Expenses': row.expenses,
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'P&L Revenue');

    // By-unit sheet
    const unitRows = Object.entries(revenue.combined_by_unit || {}).map(([unit, data]) => ({
      Unit: unit,
      'CC Revenue': data.cc,
      'Cash Revenue': data.cash,
      Total: data.total,
    }));
    const ws2 = XLSX.utils.json_to_sheet(unitRows);
    XLSX.utils.book_append_sheet(wb, ws2, 'Revenue by Unit');

    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `pl_report_${startDate}_to_${endDate}.xlsx`);
  };

  // Unit order for the revenue table
  // Units with no revenue in the period render as null, so listing Dog Wash
  // here is inert for washes that don't have one. Numbered dog washes fall
  // through to the not-in-UNIT_ORDER block below, same as numbered vacuums.
  const UNIT_ORDER = ['SS Bay 1', 'SS Bay 2', 'SS Bay 3', 'SS Bay 4', 'Auto Wash', 'Vacuums', 'Vending', 'Dog Wash', 'Other'];

  // Chart colors
  const WASH_COLORS = {
    'SS Bay 1': '#3b82f6',  // blue
    'SS Bay 2': '#10b981',  // emerald
    'SS Bay 3': '#f59e0b',  // amber
    'SS Bay 4': '#8b5cf6',  // violet
    'Auto Wash': '#ef4444', // red
  };
  const WASH_UNITS = ['SS Bay 1', 'SS Bay 2', 'SS Bay 3', 'SS Bay 4', 'Auto Wash'];

  const VAC_COLORS = [
    '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4', '#ec4899'
  ];

  // Aggregate daily data into weekly/monthly/yearly buckets, or 30-day trailing sums
  const aggregateData = (dailyData, keys, granularity, opts = {}) => {
    if (granularity === 'daily') {
      return dailyData.map(d => {
        const entry = { date: new Date(d.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) };
        keys.forEach(k => { entry[k] = d[k] || 0; });
        return entry;
      });
    }

    if (granularity === 'trailing') {
      // Rolling N-day sum ending at each day in [opts.displayStart, opts.displayEnd].
      // Iterates calendar days so missing (zero-activity) days count as $0.
      const N = opts.trailingDays || 30;
      if (!opts.displayStart || !opts.displayEnd) return [];
      const byDate = {};
      dailyData.forEach(d => { byDate[d.date] = d; });
      const result = [];
      const dt = new Date(opts.displayStart + 'T12:00:00');
      const endDt = new Date(opts.displayEnd + 'T12:00:00');
      while (dt <= endDt) {
        const entry = { date: dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) };
        keys.forEach(k => { entry[k] = 0; });
        const walk = new Date(dt);
        walk.setDate(dt.getDate() - (N - 1));
        while (walk <= dt) {
          const walkStr = walk.toISOString().slice(0, 10);
          const dayData = byDate[walkStr];
          if (dayData) keys.forEach(k => { entry[k] += (dayData[k] || 0); });
          walk.setDate(walk.getDate() + 1);
        }
        result.push(entry);
        dt.setDate(dt.getDate() + 1);
      }
      return result;
    }

    const buckets = {};
    dailyData.forEach(d => {
      const dt = new Date(d.date + 'T12:00:00');
      let key;
      if (granularity === 'weekly') {
        // Week starting Monday
        const day = dt.getDay();
        const monday = new Date(dt);
        monday.setDate(dt.getDate() - ((day + 6) % 7));
        key = monday.toISOString().slice(0, 10);
      } else if (granularity === 'monthly') {
        key = d.date.slice(0, 7); // YYYY-MM
      } else {
        key = d.date.slice(0, 4); // YYYY
      }
      if (!buckets[key]) buckets[key] = { _key: key };
      keys.forEach(k => {
        buckets[key][k] = (buckets[key][k] || 0) + (d[k] || 0);
      });
    });

    return Object.values(buckets)
      .sort((a, b) => a._key.localeCompare(b._key))
      .map(b => {
        let label;
        if (granularity === 'weekly') {
          label = 'Wk ' + new Date(b._key + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        } else if (granularity === 'monthly') {
          const [y, m] = b._key.split('-');
          label = new Date(Number(y), Number(m) - 1).toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
        } else {
          label = b._key;
        }
        const entry = { date: label };
        keys.forEach(k => { entry[k] = Math.round(b[k] * 100) / 100; });
        return entry;
      });
  };

  // Prepare chart data from daily_by_unit — trailing uses extended-range source
  const chartSource = chartGranularity === 'trailing' ? chartRevenue : revenue;
  const aggOpts = { displayStart: startDate, displayEnd: endDate, trailingDays };

  const washChartData = useMemo(() => {
    if (!chartSource?.daily_by_unit) return [];
    const filtered = chartSource.daily_by_unit.filter(d => WASH_UNITS.some(u => d[u]));
    return aggregateData(filtered, WASH_UNITS, chartGranularity, aggOpts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartSource, chartGranularity, startDate, endDate]);

  const vacChartData = useMemo(() => {
    if (!chartSource?.daily_by_unit) return [];
    const vacKeys = [];
    for (let i = 1; i <= 7; i++) vacKeys.push(`Vacuum ${i}`);
    const filtered = chartSource.daily_by_unit.filter(d => vacKeys.some(v => d[v]));
    return aggregateData(filtered, vacKeys, chartGranularity, aggOpts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartSource, chartGranularity, startDate, endDate]);

  // Validate trailing availability: user's start must be >= location data start + (trailingDays - 1) days
  const trailingWarmupShort = useMemo(() => {
    if (chartGranularity !== 'trailing') return false;
    const locStart = revenue?.location_data_start;
    if (!locStart) return false; // unknown — don't block
    const startDt = new Date(startDate + 'T12:00:00');
    const locStartDt = new Date(locStart + 'T12:00:00');
    const diffDays = Math.floor((startDt - locStartDt) / 86400000);
    return diffDays < (trailingDays - 1);
  }, [chartGranularity, revenue, startDate, trailingDays]);

  const vacKeys = ['Vacuum 1', 'Vacuum 2', 'Vacuum 3', 'Vacuum 4', 'Vacuum 5', 'Vacuum 6', 'Vacuum 7'];

  const chartTooltipFormatter = (value) => `$${Number(value).toFixed(2)}`;

  const fmtDollars = (n) => `$${Math.round(Number(n) || 0).toLocaleString('en-US')}`;

  const TooltipWithTotal = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    const total = payload.reduce((s, p) => s + (Number(p.value) || 0), 0);
    return (
      <div className="bg-white border border-gray-200 rounded shadow-md px-3 py-2 text-sm">
        <div className="font-semibold text-gray-900 mb-1">{label}</div>
        {payload.map((p, i) => (
          <div key={i} className="flex justify-between gap-4" style={{ color: p.color }}>
            <span>{p.name}</span>
            <span>{fmtDollars(p.value)}</span>
          </div>
        ))}
        <div className="flex justify-between gap-4 border-t border-gray-200 mt-1 pt-1 font-semibold text-gray-900">
          <span>Total</span>
          <span>{fmtDollars(total)}</span>
        </div>
      </div>
    );
  };
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
  const xAxisProps = (dataLength) => ({
    dataKey: 'date',
    tick: { fontSize: isMobile ? 10 : 12, angle: isMobile ? -45 : 0, textAnchor: isMobile ? 'end' : 'middle' },
    interval: isMobile ? Math.max(0, Math.floor(dataLength / 6)) : 'preserveStartEnd',
    height: isMobile ? 60 : 30,
  });

  // Histogram colors — reuse wash colors for unit breakdown
  const HIST_UNIT_COLORS = {
    'SS Bay 1': '#3b82f6',  // blue
    'SS Bay 2': '#10b981',  // emerald
    'SS Bay 3': '#f59e0b',  // amber
    'SS Bay 4': '#8b5cf6',  // violet
    'Auto Wash': '#ef4444', // red
  };
  const HIST_PAYMENT_COLORS = {
    'cc': '#3b82f6',
    'cash': '#10b981',
  };

  // Wash package mix colors — positional, so every user gets consistent
  // color-by-tier-order regardless of their menu prices.
  const PACKAGE_FALLBACK_COLORS = ['#3b82f6', '#10b981', '#ec4899', '#6366f1', '#f97316', '#14b8a6'];
  const getPackageColor = (_pkg, idx) => PACKAGE_FALLBACK_COLORS[idx % PACKAGE_FALLBACK_COLORS.length];

  const formatHour = (h) => {
    const suffix = h >= 12 ? 'PM' : 'AM';
    const display = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${display}:00 ${suffix}`;
  };

  const handleHourRangeChange = async (field, value) => {
    const val = parseInt(value);
    if (field === 'start') setHourStart(val);
    else setHourEnd(val);
    try {
      await updateSettings({ [field === 'start' ? 'operating_hours_start' : 'operating_hours_end']: val });
      refreshLocations();
    } catch (e) { /* silent */ }
  };

  // Filter hours to the configured display range
  const hourChartData = useMemo(() => {
    if (!histograms?.by_hour) return [];
    return histograms.by_hour.filter(h => h.hour >= hourStart && h.hour <= hourEnd);
  }, [histograms, hourStart, hourEnd]);

  const dowChartData = useMemo(() => {
    if (!histograms?.by_dow) return [];
    return histograms.by_dow;
  }, [histograms]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex justify-between items-center">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Profit & Loss</h1>
          <div className="hidden sm:flex space-x-3">
            <Link
              to="/cash-outs"
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium text-sm"
            >
              View All Cash Outs
            </Link>
            <button
              onClick={() => setShowUploadModal(true)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium text-sm"
            >
              Upload CC Transactions
            </button>
            <button
              onClick={() => { setShowEverWashModal(true); setEverWashError(null); setEverWashResult(null); }}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium text-sm"
            >
              Upload EverWash
            </button>
            <button
              onClick={() => { setShowUtilityModal(true); setUtilityError(null); setUtilityResult(null); setUtilityParsed(null); setUtilityPendingFile(null);}}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium text-sm"
            >
              Upload Bill
            </button>
            <Link
              to="/cash-out/new"
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium text-sm"
            >
              New Cash Out
            </Link>
          </div>
        </div>
        {/* Mobile action buttons */}
        <div className="sm:hidden grid grid-cols-5 gap-1.5 mt-3">
          <Link
            to="/cash-outs"
            className="px-1 py-1.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium text-xs text-center flex items-center justify-center"
          >
            Cash Outs
          </Link>
          <button
            onClick={() => setShowUploadModal(true)}
            className="px-1 py-1.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium text-xs text-center flex items-center justify-center"
          >
            Upload CC
          </button>
          <button
            onClick={() => { setShowEverWashModal(true); setEverWashError(null); setEverWashResult(null); }}
            className="px-1 py-1.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium text-xs text-center flex items-center justify-center"
          >
            EverWash
          </button>
          <button
            onClick={() => { setShowUtilityModal(true); setUtilityError(null); setUtilityResult(null); setUtilityParsed(null); setUtilityPendingFile(null);}}
            className="px-1 py-1.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium text-xs text-center flex items-center justify-center"
          >
            Upload Bill
          </button>
          <Link
            to="/cash-out/new"
            className="bg-blue-600 hover:bg-blue-700 text-white px-1 py-1.5 rounded-lg font-medium text-xs text-center flex items-center justify-center"
          >
            New Cash Out
          </Link>
        </div>
      </div>

      {/* CC Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-black bg-opacity-40" onClick={() => setShowUploadModal(false)} />
            <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full p-6 z-10">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Upload CC Transactions</h2>
                <button onClick={() => setShowUploadModal(false)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
              </div>

              {/* Upload area */}
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                  dragOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
                }`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => { handleDrop(e); }}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={(e) => handleFileUpload(e.target.files[0])}
                />
                {uploading ? (
                  <p className="text-gray-500">Uploading and processing...</p>
                ) : (
                  <>
                    <p className="text-gray-600 font-medium">Drop a CryptoPay CSV file here or click to browse</p>
                    <p className="text-sm text-gray-400 mt-1">Accepts .csv files</p>
                  </>
                )}
              </div>

              {/* Upload result */}
              {uploadResult && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-green-800 font-medium">Upload complete</p>
                  <p className="text-sm text-green-700">
                    {uploadResult.new_rows_imported} new transactions imported
                    {uploadResult.duplicates_skipped > 0 && ` (${uploadResult.duplicates_skipped} duplicates skipped)`}
                  </p>
                </div>
              )}

              {/* Group-total mismatches. The rows still imported — this flags
                  that the file didn't match our reading of CryptoPay's format,
                  which is the thing that would quietly skew revenue. */}
              {uploadResult?.total_charge_warnings?.length > 0 && (
                <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-amber-900 font-medium">
                    {uploadResult.total_charge_warnings.length} transaction group
                    {uploadResult.total_charge_warnings.length === 1 ? '' : 's'} didn't match TotalCharge
                  </p>
                  <p className="text-xs text-amber-800 mt-1">
                    Charges within a CryptoPay group should sum to that group's TotalCharge.
                    These rows were imported — check them before trusting the totals.
                  </p>
                  <ul className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                    {uploadResult.total_charge_warnings.map((w, i) => (
                      <li key={i} className="text-xs text-amber-900 font-mono">{w}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Error */}
              {error && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-800">{error}</p>
                </div>
              )}

              {/* Past uploads */}
              {uploads.length > 0 && (
                <div className="mt-4">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Past Uploads</h3>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {uploads.map(u => (
                      <div key={u.id} className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-3">
                        <div className="min-w-0 mr-3">
                          <span className="font-medium text-gray-900 text-sm">{u.filename}</span>
                          <p className="text-xs text-gray-500 truncate">
                            {u.transaction_count} txns &middot; {fmt(u.total_amount)}
                            {u.date_range_start && ` &middot; ${u.date_range_start} to ${u.date_range_end}`}
                          </p>
                        </div>
                        <button
                          onClick={() => handleDeleteUpload(u.id)}
                          className="text-red-600 hover:text-red-800 text-sm font-medium flex-shrink-0"
                        >
                          Delete
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Utility Bill Modal */}
      {showUtilityModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-black bg-opacity-40" onClick={() => setShowUtilityModal(false)} />
            <div className="relative bg-white rounded-lg shadow-xl max-w-3xl w-full p-6 z-10 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Upload Bill</h2>
                <button onClick={() => setShowUtilityModal(false)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
              </div>

              {/* Category selector */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  value={utilityCategory}
                  onChange={(e) => { setUtilityCategory(e.target.value); setUtilityError(null); setUtilityResult(null); setUtilityParsed(null); setUtilityPendingFile(null);}}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
                >
                  {BILL_CATEGORIES.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>

              {/* PDF Upload */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Upload PDF Bill</label>
                <div className="flex items-center space-x-3">
                  <input
                    ref={utilityFileRef}
                    type="file"
                    accept=".pdf"
                    className="text-sm text-gray-500 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    onChange={(e) => handleUtilityUpload(e.target.files[0])}
                    disabled={utilityUploading}
                  />
                </div>
                {utilityUploading && <p className="text-sm text-gray-500 mt-1">Parsing bill...</p>}
              </div>

              {/* Divider */}
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
                <div className="relative flex justify-center"><span className="bg-white px-3 text-sm text-gray-400">or enter manually</span></div>
              </div>

              {/* Manual entry form */}
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600">Provider</label>
                  <input
                    type="text"
                    placeholder="e.g. UGI, Republic Services"
                    value={utilityManual.provider}
                    onChange={(e) => setUtilityManual(prev => ({ ...prev, provider: e.target.value }))}
                    className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600">Amount ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={utilityManual.amount}
                    onChange={(e) => setUtilityManual(prev => ({ ...prev, amount: e.target.value }))}
                    className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600">Period Start</label>
                    <input
                      type="date"
                      value={utilityManual.period_start}
                      onChange={(e) => setUtilityManual(prev => ({ ...prev, period_start: e.target.value }))}
                      className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600">Period End</label>
                    <input
                      type="date"
                      value={utilityManual.period_end}
                      onChange={(e) => setUtilityManual(prev => ({ ...prev, period_end: e.target.value }))}
                      className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                </div>
                <button
                  onClick={handleUtilityManualSubmit}
                  disabled={utilityUploading}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-md text-sm font-medium disabled:opacity-50"
                >
                  Save Bill
                </button>
              </div>

              {/* Result */}
              {utilityResult && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-green-800 font-medium">Bill saved</p>
                  <p className="text-sm text-green-700">
                    {categoryLabel(utilityResult.bill?.category)} &mdash; {fmt(utilityResult.bill?.amount)}
                    {utilityResult.bill?.period_start && ` (${utilityResult.bill.period_start} to ${utilityResult.bill.period_end})`}
                  </p>
                </div>
              )}

              {/* Error */}
              {utilityError && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-800 text-sm">{utilityError}</p>
                  {utilityParsed && (
                    <div className="mt-2 text-xs text-red-700">
                      <p>Parsed: {utilityParsed.provider && `Provider: ${utilityParsed.provider}`}
                        {utilityParsed.amount && ` | Amount: $${utilityParsed.amount}`}
                        {utilityParsed.period_start && ` | Start: ${utilityParsed.period_start}`}
                        {utilityParsed.period_end && ` | End: ${utilityParsed.period_end}`}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Bill Timeline (Gantt chart) */}
              {(() => {
                if (utilityBills.length === 0) return null;

                // Compute timeline range: 3 months back from today, scrollable
                const today = new Date();
                // Find earliest bill to set timeline start
                const allStarts = utilityBills.map(b => new Date(b.period_start + 'T00:00:00'));
                const allEnds = utilityBills.map(b => new Date(b.period_end + 'T00:00:00'));
                const earliestBill = new Date(Math.min(...allStarts));
                // Timeline starts at the 1st of the earliest bill's month (or 6 months ago, whichever is earlier)
                const sixMonthsAgo = new Date(today.getFullYear(), today.getMonth() - 6, 1);
                const timelineStart = new Date(Math.min(earliestBill.getTime(), sixMonthsAgo.getTime()));
                timelineStart.setDate(1); // snap to 1st of month
                // Timeline ends at end of next month
                const timelineEnd = new Date(today.getFullYear(), today.getMonth() + 2, 0);
                const totalDays = Math.ceil((timelineEnd - timelineStart) / (1000 * 60 * 60 * 24));
                const dayWidth = 4; // px per day
                const totalWidth = totalDays * dayWidth;

                // Build month labels
                const months = [];
                let m = new Date(timelineStart);
                while (m <= timelineEnd) {
                  const monthStart = new Date(m);
                  const monthEnd = new Date(m.getFullYear(), m.getMonth() + 1, 0);
                  const startDay = Math.max(0, Math.ceil((monthStart - timelineStart) / (1000 * 60 * 60 * 24)));
                  const endDay = Math.min(totalDays, Math.ceil((monthEnd - timelineStart) / (1000 * 60 * 60 * 24)));
                  months.push({
                    label: monthStart.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
                    left: startDay * dayWidth,
                    width: (endDay - startDay) * dayWidth,
                  });
                  m = new Date(m.getFullYear(), m.getMonth() + 1, 1);
                }

                // Group bills by category
                const categories = BILL_CATEGORIES.filter(cat =>
                  utilityBills.some(b => b.category === cat.value)
                );

                const ROW_HEIGHT = 36;
                const HEADER_HEIGHT = 24;
                const BAR_HEIGHT = 22;

                const getBarStyle = (bill) => {
                  const start = new Date(bill.period_start + 'T00:00:00');
                  const end = new Date(bill.period_end + 'T00:00:00');
                  const startDay = Math.max(0, (start - timelineStart) / (1000 * 60 * 60 * 24));
                  const endDay = Math.min(totalDays, (end - timelineStart) / (1000 * 60 * 60 * 24));
                  return {
                    left: startDay * dayWidth,
                    width: Math.max((endDay - startDay) * dayWidth, 4),
                  };
                };

                // Default scroll to show last 3 months
                const scrollRef = React.createRef();
                const threeMonthsAgo = new Date(today.getFullYear(), today.getMonth() - 2, 1);
                const defaultScroll = Math.max(0, Math.ceil((threeMonthsAgo - timelineStart) / (1000 * 60 * 60 * 24)) * dayWidth - 20);

                return (
                  <div className="mt-5">
                    <h3 className="text-sm font-medium text-gray-700 mb-2">Bill Timeline</h3>

                    {/* Edit form (shown above chart when editing) */}
                    {editingBill && (() => {
                      const bill = utilityBills.find(b => b.id === editingBill);
                      return (
                        <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <p className="text-xs font-medium text-blue-800 mb-2">
                            Editing: {bill ? `${categoryLabel(bill.category)}${bill.provider ? ` — ${bill.provider}` : ''}` : ''}
                          </p>
                          <div className="grid grid-cols-3 gap-2">
                            <div>
                              <label className="block text-xs text-gray-600">Amount</label>
                              <input type="number" step="0.01" value={editForm.amount}
                                onChange={(e) => setEditForm(prev => ({ ...prev, amount: e.target.value }))}
                                className="w-full border border-gray-300 rounded px-2 py-1 text-sm" />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600">Start</label>
                              <input type="date" value={editForm.period_start}
                                onChange={(e) => setEditForm(prev => ({ ...prev, period_start: e.target.value }))}
                                className="w-full border border-gray-300 rounded px-2 py-1 text-sm" />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600">End</label>
                              <input type="date" value={editForm.period_end}
                                onChange={(e) => setEditForm(prev => ({ ...prev, period_end: e.target.value }))}
                                className="w-full border border-gray-300 rounded px-2 py-1 text-sm" />
                            </div>
                          </div>
                          <div className="flex space-x-2 mt-2">
                            <button onClick={handleSaveEdit} className="bg-blue-600 text-white px-3 py-1 rounded text-sm font-medium hover:bg-blue-700">Save</button>
                            <button onClick={() => setEditingBill(null)} className="text-gray-600 hover:text-gray-800 text-sm px-3 py-1">Cancel</button>
                          </div>
                        </div>
                      );
                    })()}

                    <div className="flex border border-gray-200 rounded-lg overflow-hidden">
                      {/* Category labels (fixed left column) */}
                      <div className="flex-shrink-0 bg-gray-50 border-r border-gray-200" style={{ width: 100 }}>
                        <div className="border-b border-gray-200" style={{ height: HEADER_HEIGHT }} />
                        {categories.map(cat => (
                          <div key={cat.value} className="flex items-center px-2 border-b border-gray-100 text-xs font-medium text-gray-700 truncate" style={{ height: ROW_HEIGHT }}>
                            {cat.label}
                          </div>
                        ))}
                      </div>

                      {/* Scrollable timeline */}
                      <div
                        className="overflow-x-auto flex-1"
                        ref={(el) => { if (el && !el.dataset.scrolled) { el.scrollLeft = defaultScroll; el.dataset.scrolled = '1'; } }}
                      >
                        <div style={{ width: totalWidth, position: 'relative' }}>
                          {/* Month headers */}
                          <div className="flex border-b border-gray-200" style={{ height: HEADER_HEIGHT }}>
                            {months.map((m, i) => (
                              <div
                                key={i}
                                className="text-xs text-gray-500 text-center border-r border-gray-100 flex items-center justify-center"
                                style={{ position: 'absolute', left: m.left, width: m.width, height: HEADER_HEIGHT }}
                              >
                                {m.label}
                              </div>
                            ))}
                          </div>

                          {/* Rows */}
                          {categories.map(cat => {
                            const catBills = utilityBills
                              .filter(b => b.category === cat.value)
                              .sort((a, b) => a.period_start.localeCompare(b.period_start));
                            return (
                              <div key={cat.value} className="relative border-b border-gray-100" style={{ height: ROW_HEIGHT }}>
                                {/* Month grid lines */}
                                {months.map((m, i) => (
                                  <div key={i} className="absolute top-0 bottom-0 border-r border-gray-100" style={{ left: m.left + m.width }} />
                                ))}
                                {/* Bill bars */}
                                {catBills.map(bill => {
                                  const bar = getBarStyle(bill);
                                  const isEditing = editingBill === bill.id;
                                  return (
                                    <div
                                      key={bill.id}
                                      className={`absolute rounded group cursor-pointer transition-colors ${
                                        isEditing ? 'bg-blue-400 ring-2 ring-blue-600' : 'bg-blue-500 hover:bg-blue-600'
                                      }`}
                                      style={{
                                        left: bar.left,
                                        width: bar.width,
                                        height: BAR_HEIGHT,
                                        top: (ROW_HEIGHT - BAR_HEIGHT) / 2,
                                      }}
                                      title={`${categoryLabel(bill.category)}${bill.provider ? ` — ${bill.provider}` : ''}\n$${bill.amount.toFixed(2)}\n${bill.period_start} to ${bill.period_end}`}
                                      onClick={() => handleEditBill(bill)}
                                    >
                                      {/* Amount label on bar */}
                                      {bar.width > 40 && (
                                        <span className="absolute inset-0 flex items-center justify-center text-white text-[10px] font-medium truncate px-1">
                                          ${bill.amount.toFixed(0)}
                                        </span>
                                      )}
                                      {/* Delete button on hover */}
                                      <button
                                        className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full text-[9px] leading-none items-center justify-center hidden group-hover:flex hover:bg-red-600"
                                        onClick={(e) => { e.stopPropagation(); handleDeleteBill(bill.id); }}
                                        title="Delete"
                                      >
                                        &times;
                                      </button>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })}

                          {/* Today marker */}
                          {(() => {
                            const todayDay = (today - timelineStart) / (1000 * 60 * 60 * 24);
                            if (todayDay >= 0 && todayDay <= totalDays) {
                              return (
                                <div
                                  className="absolute top-0 bottom-0 border-l-2 border-red-400 opacity-50 pointer-events-none"
                                  style={{ left: todayDay * dayWidth, height: HEADER_HEIGHT + categories.length * ROW_HEIGHT }}
                                />
                              );
                            }
                            return null;
                          })()}
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">Click a bar to edit. Hover for details. Scroll to see more history.</p>
                  </div>
                );
              })()}

              {/* Uploaded Bills — download individual or bulk-zip */}
              {utilityBills.length > 0 && (
                <div className="mt-6 border-t border-gray-200 pt-4">
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900">Uploaded Bills</h3>
                      <p className="text-xs text-gray-500">
                        Download individual PDFs or select multiple to get a zip.
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {selectedBillIds.size > 0 && (
                        <span className="text-xs text-gray-500">{selectedBillIds.size} selected</span>
                      )}
                      <button
                        onClick={handleDownloadSelected}
                        disabled={selectedBillIds.size === 0 || bulkDownloading}
                        className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-3 py-1.5 rounded-md text-xs font-medium"
                      >
                        {bulkDownloading ? 'Zipping…' : 'Download Selected'}
                      </button>
                      {selectedBillIds.size > 0 && (
                        <button
                          onClick={() => setSelectedBillIds(new Set())}
                          className="text-xs text-gray-600 hover:text-gray-900 px-1"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="border border-gray-200 rounded-md overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 text-xs">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="w-8 px-2 py-2 text-left">
                            <input
                              type="checkbox"
                              checked={allSelectableSelected}
                              onChange={toggleSelectAllBills}
                              disabled={selectableBills.length === 0}
                              className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                            />
                          </th>
                          <th className="px-2 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Category</th>
                          <th className="px-2 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Period</th>
                          <th className="px-2 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Provider</th>
                          <th className="px-2 py-2 text-right font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                          <th className="px-2 py-2 text-right font-medium text-gray-500 uppercase tracking-wider">Download</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {[...utilityBills]
                          .sort((a, b) => b.period_end.localeCompare(a.period_end))
                          .map((bill) => {
                            const hasFile = !!bill.has_file;
                            const selected = selectedBillIds.has(bill.id);
                            return (
                              <tr key={bill.id} className={hasFile ? 'hover:bg-gray-50' : 'bg-gray-50 text-gray-400'}>
                                <td className="px-2 py-2">
                                  <input
                                    type="checkbox"
                                    checked={selected}
                                    onChange={() => toggleBillSelected(bill.id)}
                                    disabled={!hasFile}
                                    className="h-4 w-4 text-blue-600 border-gray-300 rounded disabled:opacity-40"
                                    title={hasFile ? '' : 'No file attached (uploaded before this feature launched)'}
                                  />
                                </td>
                                <td className="px-2 py-2 whitespace-nowrap font-medium">{categoryLabel(bill.category)}</td>
                                <td className="px-2 py-2 whitespace-nowrap">{bill.period_start} — {bill.period_end}</td>
                                <td className="px-2 py-2 whitespace-nowrap">{bill.provider || '—'}</td>
                                <td className="px-2 py-2 whitespace-nowrap text-right font-medium">{fmt(bill.amount)}</td>
                                <td className="px-2 py-2 whitespace-nowrap text-right">
                                  {hasFile ? (
                                    <button
                                      onClick={() => handleDownloadBill(bill)}
                                      className="text-blue-600 hover:text-blue-800 font-medium"
                                    >
                                      Download
                                    </button>
                                  ) : (
                                    <span className="text-gray-400">No file</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Date Range Filter */}
      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <div className="flex flex-col gap-2">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            {/* Mobile: dropdown */}
            <div className="sm:hidden flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700 shrink-0">Date Range</label>
              <select
                value={datePreset}
                onChange={(e) => {
                  const val = e.target.value;
                  setDatePreset(val);
                  if (val !== 'custom') {
                    const days = val === 'all' ? null : Number(val);
                    setStartDate(days ? new Date(Date.now() - days * 86400000).toISOString().split('T')[0] : '2020-01-01');
                    setEndDate(today);
                  }
                }}
                className="border border-gray-300 rounded-md px-2 py-2 text-sm flex-1 bg-white"
              >
                <option value="30">Last 30 days</option>
                <option value="90">Last 3 months</option>
                <option value="182">Last 6 months</option>
                <option value="365">Last year</option>
                <option value="all">All time</option>
                <option value="custom">Custom</option>
              </select>
            </div>
            {/* Desktop: buttons */}
            <div className="hidden sm:flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700 mr-1">Date Range</label>
              {[
                { label: '30 days', value: '30', days: 30 },
                { label: '3 months', value: '90', days: 90 },
                { label: '6 months', value: '182', days: 182 },
                { label: '1 year', value: '365', days: 365 },
                { label: 'All time', value: 'all', days: null },
                { label: 'Custom', value: 'custom', days: undefined },
              ].map(preset => (
                <button
                  key={preset.value}
                  onClick={() => {
                    setDatePreset(preset.value);
                    if (preset.value !== 'custom') {
                      setStartDate(preset.days != null ? new Date(Date.now() - preset.days * 86400000).toISOString().split('T')[0] : '2020-01-01');
                      setEndDate(today);
                    }
                  }}
                  className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                    datePreset === preset.value
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {preset.label}
                </button>
              ))}
            </div>
            <button
              onClick={handleExport}
              className="sm:ml-auto px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium text-sm"
            >
              Export Excel
            </button>
          </div>
          {/* Custom date inputs — only shown when "Custom" selected */}
          {datePreset === 'custom' && (
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="border border-gray-300 rounded-md px-2 sm:px-3 py-2 text-sm flex-1 min-w-0"
              />
              <span className="text-gray-500 text-sm">to</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="border border-gray-300 rounded-md px-2 sm:px-3 py-2 text-sm flex-1 min-w-0"
              />
            </div>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      {revenue && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white rounded-lg shadow p-5">
            <p className="text-sm text-gray-500">Total Revenue</p>
            <p className="text-2xl font-bold text-gray-900">
              {fmt(revenue.total_revenue ?? ((revenue.cc_revenue?.total || 0) + (revenue.cash_revenue?.total || 0)))}
            </p>
            {revenue.everwash?.gross_revenue > 0 && (
              <p className="text-xs text-gray-500 mt-1">
                Incl. {fmt(revenue.everwash.gross_revenue)} EverWash
              </p>
            )}
          </div>
          <div className="bg-white rounded-lg shadow p-5">
            <p className="text-sm text-gray-500">CC Revenue</p>
            <p className="text-2xl font-bold text-blue-600">{fmt(revenue.cc_revenue?.total)}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-5">
            <p className="text-sm text-gray-500">Cash Revenue</p>
            <p className="text-2xl font-bold text-green-600">{fmt(revenue.cash_revenue?.total)}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-5">
            <p className="text-sm text-gray-500">Total Expenses</p>
            <p className="text-2xl font-bold text-red-600">{fmt(revenue.expenses?.total)}</p>
            <div className="mt-1 text-xs text-gray-500 space-y-0.5">
              {revenue.expenses?.maintenance > 0 && <p>Maintenance: {fmt(revenue.expenses.maintenance)}</p>}
              {revenue.expenses?.utilities > 0 && <p>Utilities: {fmt(revenue.expenses.utilities)}</p>}
              <p>Soap/Chemicals: {fmt(revenue.expenses?.soap)}</p>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-5">
            <p className="text-sm text-gray-500">Net P&L</p>
            <p className={`text-2xl font-bold ${revenue.net >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {fmt(revenue.net)}
            </p>
            {revenue.sales_tax > 0 && (
              <p className="text-xs text-gray-500 mt-1">After {fmt(revenue.sales_tax)} sales tax</p>
            )}
          </div>
        </div>
      )}

      {/* Revenue Over Time — Granularity Selector + Charts */}
      {(washChartData.length > 0 || vacChartData.length > 0 || chartGranularity === 'trailing') && (
        <>
          {/* Granularity toggle — dropdown on mobile, buttons on desktop */}
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-sm font-medium text-gray-700">View by:</span>
            <div className="sm:hidden">
              <select
                value={chartGranularity}
                onChange={(e) => setChartGranularity(e.target.value)}
                className="border border-gray-300 rounded-md px-2 py-1 text-sm bg-white"
              >
                <option value="daily">Daily</option>
                <option value="trailing">Trailing Total</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
              </select>
            </div>
            <div className="hidden sm:flex gap-1 flex-wrap">
              {[
                { v: 'daily', label: 'Daily' },
                { v: 'trailing', label: 'Trailing Total' },
                { v: 'weekly', label: 'Weekly' },
                { v: 'monthly', label: 'Monthly' },
                { v: 'yearly', label: 'Yearly' },
              ].map(({ v, label }) => (
                <button
                  key={v}
                  onClick={() => setChartGranularity(v)}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                    chartGranularity === v
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            {chartGranularity === 'trailing' && (
              <label className="flex items-center gap-2 text-sm text-gray-600">
                Period:
                <select
                  value={trailingDays}
                  onChange={(e) => setTrailingDays(parseInt(e.target.value, 10))}
                  className="border border-gray-300 rounded-md px-2 py-1 text-sm bg-white"
                >
                  <option value={30}>30 days</option>
                  <option value={60}>60 days</option>
                  <option value={90}>90 days</option>
                  <option value={180}>180 days</option>
                  <option value={365}>365 days</option>
                </select>
              </label>
            )}
          </div>
        </>
      )}

      {trailingWarmupShort && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
          <strong>{trailingDays}-day trailing needs at least {trailingDays} days of prior data.</strong>{' '}
          Your data starts on{' '}
          {revenue?.location_data_start
            ? new Date(revenue.location_data_start + 'T12:00:00').toLocaleDateString()
            : 'an unknown date'}
          . Pick a start date on or after{' '}
          {revenue?.location_data_start
            ? new Date(new Date(revenue.location_data_start + 'T12:00:00').getTime() + (trailingDays - 1) * 86400000).toLocaleDateString()
            : '—'}
          {' '}to use this view (or choose a shorter trailing period).
        </div>
      )}

      {/* Wash Bay Revenue Chart */}
      {!trailingWarmupShort && washChartData.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Wash Bay Revenue Over Time
            {chartGranularity === 'trailing' && <span className="text-sm font-normal text-gray-500 ml-2">({trailingDays}-day trailing sum)</span>}
          </h2>
          <ResponsiveContainer width="100%" height={350}>
            {chartGranularity === 'daily' || chartGranularity === 'trailing' ? (
              <AreaChart data={washChartData} margin={{ left: 0, right: 8, top: 5, bottom: isMobile ? 10 : 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis {...xAxisProps(washChartData.length)} />
                <YAxis tickFormatter={(v) => `$${v}`} tick={{ fontSize: 12 }} width={45} />
                <Tooltip content={<TooltipWithTotal />} />
                <Legend />
                {WASH_UNITS.map(unit => (
                  <Area
                    key={unit}
                    type="linear"
                    dataKey={unit}
                    stackId="wash"
                    fill={WASH_COLORS[unit]}
                    stroke={WASH_COLORS[unit]}
                    fillOpacity={0.7}
                  />
                ))}
              </AreaChart>
            ) : (
              <BarChart data={washChartData} margin={{ left: 0, right: 8, top: 5, bottom: isMobile ? 10 : 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis {...xAxisProps(washChartData.length)} />
                <YAxis tickFormatter={(v) => `$${v}`} tick={{ fontSize: 12 }} width={55} />
                <Tooltip content={<TooltipWithTotal />} />
                <Legend />
                {WASH_UNITS.map(unit => (
                  <Bar key={unit} dataKey={unit} stackId="wash" fill={WASH_COLORS[unit]} />
                ))}
              </BarChart>
            )}
          </ResponsiveContainer>
        </div>
      )}

      {/* Vacuum Revenue Chart */}
      {!trailingWarmupShort && vacChartData.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Vacuum Revenue Over Time
            {chartGranularity === 'trailing' && <span className="text-sm font-normal text-gray-500 ml-2">({trailingDays}-day trailing sum)</span>}
          </h2>
          <ResponsiveContainer width="100%" height={350}>
            {chartGranularity === 'daily' || chartGranularity === 'trailing' ? (
              <AreaChart data={vacChartData} margin={{ left: 0, right: 8, top: 5, bottom: isMobile ? 10 : 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis {...xAxisProps(vacChartData.length)} />
                <YAxis tickFormatter={(v) => `$${v}`} tick={{ fontSize: 12 }} width={45} />
                <Tooltip content={<TooltipWithTotal />} />
                <Legend />
                {vacKeys.map((vac, i) => (
                  <Area
                    key={vac}
                    type="linear"
                    dataKey={vac}
                    stackId="vac"
                    fill={VAC_COLORS[i]}
                    stroke={VAC_COLORS[i]}
                    fillOpacity={0.7}
                  />
                ))}
              </AreaChart>
            ) : (
              <BarChart data={vacChartData} margin={{ left: 0, right: 8, top: 5, bottom: isMobile ? 10 : 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis {...xAxisProps(vacChartData.length)} />
                <YAxis tickFormatter={(v) => `$${v}`} tick={{ fontSize: 12 }} width={55} />
                <Tooltip content={<TooltipWithTotal />} />
                <Legend />
                {vacKeys.map((vac, i) => (
                  <Bar key={vac} dataKey={vac} stackId="vac" fill={VAC_COLORS[i]} />
                ))}
              </BarChart>
            )}
          </ResponsiveContainer>
        </div>
      )}

      {/* Revenue Histograms — By Hour & By Day of Week */}
      {(hourChartData.length > 0 || dowChartData.length > 0) && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="sm:flex sm:items-center sm:justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Wash Bay Revenue Distribution</h2>
            <div className="flex items-center space-x-2 mt-2 sm:mt-0">
              <label className="text-sm font-medium text-gray-700">Breakdown:</label>
              <select
                value={histBreakdown}
                onChange={(e) => setHistBreakdown(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-1.5 text-sm bg-white"
              >
                <option value="none">Total</option>
                <option value="payment">By Payment Type</option>
                <option value="unit">By Unit</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* By Hour */}
            {hourChartData.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <h3 className="text-sm font-medium text-gray-700">
                    Avg Revenue by Hour of Day
                    <span className="text-xs text-gray-400 ml-1">(CC transactions only)</span>
                  </h3>
                  <div className="flex items-center gap-1 text-xs text-gray-500">
                    <select
                      value={hourStart}
                      onChange={(e) => handleHourRangeChange('start', e.target.value)}
                      className="border border-gray-300 rounded px-1.5 py-0.5 text-xs bg-white"
                    >
                      {Array.from({ length: 24 }, (_, i) => (
                        <option key={i} value={i}>{formatHour(i)}</option>
                      ))}
                    </select>
                    <span>–</span>
                    <select
                      value={hourEnd}
                      onChange={(e) => handleHourRangeChange('end', e.target.value)}
                      className="border border-gray-300 rounded px-1.5 py-0.5 text-xs bg-white"
                    >
                      {Array.from({ length: 24 }, (_, i) => (
                        <option key={i} value={i}>{formatHour(i)}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={hourChartData} margin={{ left: 0, right: 8, top: 5, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={1} angle={-45} textAnchor="end" height={60} />
                    <YAxis tickFormatter={(v) => `$${v}`} tick={{ fontSize: 12 }} width={45} />
                    <Tooltip formatter={chartTooltipFormatter} />
                    {histBreakdown === 'none' && (
                      <Bar dataKey="total" name="Total" fill="#3b82f6" radius={[2, 2, 0, 0]} />
                    )}
                    {histBreakdown === 'payment' && (
                      <Bar dataKey="cc" name="Credit Card" stackId="pay" fill={HIST_PAYMENT_COLORS.cc} radius={[2, 2, 0, 0]} />
                    )}
                    {histBreakdown === 'unit' && (
                      <>
                        {(histograms?.unit_groups || []).map(ug => (
                          <Bar key={ug} dataKey={ug} name={ug} stackId="unit" fill={HIST_UNIT_COLORS[ug] || '#6b7280'} />
                        ))}
                      </>
                    )}
                    <Legend />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
            {/* By Day of Week */}
            {dowChartData.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">Avg Revenue by Day of Week</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={dowChartData} margin={{ left: 0, right: 8, top: 5, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                    <YAxis tickFormatter={(v) => `$${v}`} tick={{ fontSize: 12 }} width={45} />
                    <Tooltip formatter={chartTooltipFormatter} />
                    {histBreakdown === 'none' && (
                      <Bar dataKey="total" name="Total" fill="#3b82f6" radius={[2, 2, 0, 0]} />
                    )}
                    {histBreakdown === 'payment' && (
                      <>
                        <Bar dataKey="cc" name="Credit Card" stackId="pay" fill={HIST_PAYMENT_COLORS.cc} />
                        <Bar dataKey="cash" name="Cash" stackId="pay" fill={HIST_PAYMENT_COLORS.cash} radius={[2, 2, 0, 0]} />
                      </>
                    )}
                    {histBreakdown === 'unit' && (
                      <>
                        {(histograms?.unit_groups || []).map((ug, i, arr) => (
                          <Bar
                            key={ug}
                            dataKey={ug}
                            name={ug}
                            stackId="unit"
                            fill={HIST_UNIT_COLORS[ug] || '#6b7280'}
                            radius={i === arr.length - 1 ? [2, 2, 0, 0] : undefined}
                          />
                        ))}
                      </>
                    )}
                    <Legend />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Auto Wash Package Mix */}
      {washMix && washMix.total_transactions > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="sm:flex sm:items-center sm:justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Auto Wash Package Mix</h2>
              <p className="text-sm text-gray-500">{washMix.total_transactions.toLocaleString()} transactions</p>
            </div>
            <div className="flex items-center space-x-2 mt-2 sm:mt-0">
              <label className="text-sm font-medium text-gray-700">View:</label>
              <select
                value={washMixView}
                onChange={(e) => setWashMixView(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-1.5 text-sm bg-white"
              >
                <option value="overall">Overall</option>
                <option value="hour">By Hour of Day</option>
                <option value="dow">By Day of Week</option>
              </select>
            </div>
          </div>

          {/* Overall view — horizontal bar chart */}
          {washMixView === 'overall' && (
            <>
              <ResponsiveContainer width="100%" height={Math.max(washMix.overall.length * 50 + 40, 160)}>
                <BarChart data={washMix.overall} layout="vertical" margin={{ left: 10, right: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" domain={[0, () => { const max = Math.max(...washMix.overall.map(d => d.percent)); return Math.min(100, Math.ceil(max / 10) * 10 + 10); }]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 12 }} />
                  <YAxis type="category" dataKey="package" tick={{ fontSize: 13, fontWeight: 600 }} width={50} />
                  <Tooltip
                    formatter={(value, name, props) => [`${value}% (${props.payload.count} txns)`, 'Share']}
                  />
                  <Bar dataKey="percent" radius={[0, 4, 4, 0]}>
                    {washMix.overall.map((entry, idx) => (
                      <Cell key={entry.package} fill={getPackageColor(entry.package, idx)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>

              {/* Per-package soap cost — shows which chemicals contribute to which tier */}
              {washMix.overall.some(r => 'soap_cost_per_wash' in r) && (
                <div className="mt-4 overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Package</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Share</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Transactions</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Soap cost / wash</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Soap margin / wash</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {washMix.overall.map((row) => {
                        const cost = row.soap_cost_per_wash ?? 0;
                        const margin = row.charge - cost;
                        return (
                          <tr key={row.package}>
                            <td className="px-4 py-2 font-medium text-gray-900">{row.package}</td>
                            <td className="px-4 py-2 text-right text-gray-700">{row.percent}%</td>
                            <td className="px-4 py-2 text-right text-gray-700">{row.count.toLocaleString()}</td>
                            <td className="px-4 py-2 text-right text-gray-900">${cost.toFixed(3)}</td>
                            <td className="px-4 py-2 text-right text-gray-900">${margin.toFixed(2)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <p className="text-xs text-gray-500 mt-2">
                    "Soap cost / wash" splits each Auto Wash chemical's period cost across the tiers it's assigned to.
                    Set per-chemical assignments on the inventory edit page.
                  </p>
                </div>
              )}
            </>
          )}

          {/* By Hour view — 100% stacked bar chart */}
          {washMixView === 'hour' && washMix.by_hour.length > 0 && (
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={washMix.by_hour} margin={{ left: 0, right: 8, top: 5, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={1} angle={-45} textAnchor="end" height={60} />
                <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 12 }} />
                <Tooltip
                  formatter={(value, name, props) => [`${value}%`, name]}
                  labelFormatter={(label, payload) => {
                    const entry = payload?.[0]?.payload;
                    return entry ? `${label} (${entry.total} txns)` : label;
                  }}
                />
                <Legend />
                {washMix.packages.map((pkg, idx) => (
                  <Bar key={pkg} dataKey={pkg} name={pkg} stackId="mix" fill={getPackageColor(pkg, idx)} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          )}

          {/* By Day of Week view — 100% stacked bar chart */}
          {washMixView === 'dow' && washMix.by_dow.length > 0 && (
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={washMix.by_dow} margin={{ left: 0, right: 8, top: 5, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 12 }} />
                <Tooltip
                  formatter={(value, name, props) => [`${value}%`, name]}
                  labelFormatter={(label, payload) => {
                    const entry = payload?.[0]?.payload;
                    return entry ? `${label} (${entry.total} txns)` : label;
                  }}
                />
                <Legend />
                {washMix.packages.map((pkg, idx) => (
                  <Bar key={pkg} dataKey={pkg} name={pkg} stackId="mix" fill={getPackageColor(pkg, idx)} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      )}

      {/* Revenue by Unit Table */}
      {revenue && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Revenue by Unit</h2>
          </div>
          <ScrollHint>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unit</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">CC Revenue</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Cash Revenue</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {UNIT_ORDER.map(unit => {
                  const data = revenue.combined_by_unit?.[unit];
                  if (!data) return null;
                  return (
                    <tr key={unit} className="hover:bg-gray-50">
                      <td className="px-6 py-3 text-sm font-medium text-gray-900">{unit}</td>
                      <td className="px-6 py-3 text-sm text-right text-gray-900">{fmt(data.cc)}</td>
                      <td className="px-6 py-3 text-sm text-right text-gray-900">{fmt(data.cash)}</td>
                      <td className="px-6 py-3 text-sm text-right font-bold text-gray-900">{fmt(data.total)}</td>
                    </tr>
                  );
                })}
                {/* Any units not in UNIT_ORDER */}
                {Object.keys(revenue.combined_by_unit || {})
                  .filter(u => !UNIT_ORDER.includes(u))
                  .map(unit => {
                    const data = revenue.combined_by_unit[unit];
                    return (
                      <tr key={unit} className="hover:bg-gray-50">
                        <td className="px-6 py-3 text-sm font-medium text-gray-900">{unit}</td>
                        <td className="px-6 py-3 text-sm text-right text-gray-900">{fmt(data.cc)}</td>
                        <td className="px-6 py-3 text-sm text-right text-gray-900">{fmt(data.cash)}</td>
                        <td className="px-6 py-3 text-sm text-right font-bold text-gray-900">{fmt(data.total)}</td>
                      </tr>
                    );
                  })}
                {/* Totals row — summed from the rows above so streams that
                    aren't CryptoPay CC or cash (EverWash) still add up. */}
                {(() => {
                  const units = Object.values(revenue.combined_by_unit || {});
                  const sum = (key) => units.reduce((acc, d) => acc + (d?.[key] || 0), 0);
                  return (
                    <tr className="bg-gray-50 font-bold">
                      <td className="px-6 py-3 text-sm text-gray-900">Total</td>
                      <td className="px-6 py-3 text-sm text-right text-gray-900">{fmt(sum('cc'))}</td>
                      <td className="px-6 py-3 text-sm text-right text-gray-900">{fmt(sum('cash'))}</td>
                      <td className="px-6 py-3 text-sm text-right text-gray-900">{fmt(sum('total'))}</td>
                    </tr>
                  );
                })()}
              </tbody>
            </table>
          </ScrollHint>
        </div>
      )}

      {/* Expenses Breakdown Table */}
      {revenue && revenue.expenses?.total > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Expenses Breakdown</h2>
          </div>
          <ScrollHint>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {revenue.expenses?.parts > 0 && (
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-3 text-sm text-gray-900 pl-10">Maintenance — Parts</td>
                    <td className="px-6 py-3 text-sm text-right text-gray-900">{fmt(revenue.expenses.parts)}</td>
                  </tr>
                )}
                {revenue.expenses?.labor > 0 && (
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-3 text-sm text-gray-900 pl-10">Maintenance — Labor</td>
                    <td className="px-6 py-3 text-sm text-right text-gray-900">{fmt(revenue.expenses.labor)}</td>
                  </tr>
                )}
                {revenue.expenses?.maintenance > 0 && (
                  <tr className="hover:bg-gray-50 bg-gray-50/50">
                    <td className="px-6 py-3 text-sm font-medium text-gray-900">Maintenance Subtotal</td>
                    <td className="px-6 py-3 text-sm text-right font-medium text-gray-900">{fmt(revenue.expenses.maintenance)}</td>
                  </tr>
                )}
                {Object.entries(revenue.expenses?.utilities_by_category || {}).map(([cat, amount]) => (
                  <tr key={cat} className="hover:bg-gray-50">
                    <td className="px-6 py-3 text-sm text-gray-900 pl-10">{categoryLabel(cat)}</td>
                    <td className="px-6 py-3 text-sm text-right text-gray-900">{fmt(amount)}</td>
                  </tr>
                ))}
                {revenue.expenses?.utilities > 0 && (
                  <tr className="hover:bg-gray-50 bg-gray-50/50">
                    <td className="px-6 py-3 text-sm font-medium text-gray-900">Utilities Subtotal</td>
                    <td className="px-6 py-3 text-sm text-right font-medium text-gray-900">{fmt(revenue.expenses.utilities)}</td>
                  </tr>
                )}
                {(() => {
                  const details = revenue.expenses?.soap_details || [];
                  const ssItems = details.filter(s => s.location === 'SS Bays');
                  const autoItems = details.filter(s => s.location === 'Auto Wash');
                  return (
                    <>
                      {ssItems.length > 0 && ssItems.map((s, i) => (
                        <tr key={`ss-${i}`} className="hover:bg-gray-50">
                          <td className="px-6 py-3 text-sm text-gray-900 pl-10">
                            {s.name} <span className="text-gray-400">(SS)</span>
                          </td>
                          <td className="px-6 py-3 text-sm text-right text-gray-900">
                            {fmt(s.est_cost)}
                            <span className="text-xs text-gray-400 ml-1">({s.est_jugs} jugs)</span>
                          </td>
                        </tr>
                      ))}
                      {ssItems.length > 0 && (
                        <tr className="hover:bg-gray-50 bg-gray-50/50">
                          <td className="px-6 py-3 text-sm font-medium text-gray-900 pl-10">SS Soap Subtotal</td>
                          <td className="px-6 py-3 text-sm text-right font-medium text-gray-900">{fmt(revenue.expenses?.soap_ss)}</td>
                        </tr>
                      )}
                      {autoItems.length > 0 && autoItems.map((s, i) => (
                        <tr key={`auto-${i}`} className="hover:bg-gray-50">
                          <td className="px-6 py-3 text-sm text-gray-900 pl-10">
                            {s.name} <span className="text-gray-400">(Auto)</span>
                          </td>
                          <td className="px-6 py-3 text-sm text-right text-gray-900">
                            {fmt(s.est_cost)}
                            <span className="text-xs text-gray-400 ml-1">({s.est_jugs} jugs)</span>
                          </td>
                        </tr>
                      ))}
                      {autoItems.length > 0 && (
                        <tr className="hover:bg-gray-50 bg-gray-50/50">
                          <td className="px-6 py-3 text-sm font-medium text-gray-900 pl-10">Auto Soap Subtotal</td>
                          <td className="px-6 py-3 text-sm text-right font-medium text-gray-900">{fmt(revenue.expenses?.soap_auto)}</td>
                        </tr>
                      )}
                    </>
                  );
                })()}
                <tr className="hover:bg-gray-50 bg-gray-50/50">
                  <td className="px-6 py-3 text-sm font-medium text-gray-900">Soap/Chemicals Total</td>
                  <td className="px-6 py-3 text-sm text-right font-medium text-gray-900">{fmt(revenue.expenses?.soap)}</td>
                </tr>
                {revenue.expenses?.staff_labor > 0 && (
                  <tr className="hover:bg-gray-50 bg-gray-50/50">
                    <td className="px-6 py-3 text-sm font-medium text-gray-900">Staff Labor (incl. OT)</td>
                    <td className="px-6 py-3 text-sm text-right font-medium text-gray-900">{fmt(revenue.expenses.staff_labor)}</td>
                  </tr>
                )}
                <tr className="bg-gray-50 font-bold">
                  <td className="px-6 py-3 text-sm text-gray-900">Total Expenses</td>
                  <td className="px-6 py-3 text-sm text-right text-gray-900">{fmt(revenue.expenses.total)}</td>
                </tr>
              </tbody>
            </table>
          </ScrollHint>
        </div>
      )}

      {/* P&L Summary Table */}
      {revenue && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">P&L Summary</h2>
          </div>
          <ScrollHint>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Item</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-3 text-sm text-gray-900 pl-10">Credit Card Revenue</td>
                  <td className="px-6 py-3 text-sm text-right text-gray-900">{fmt(revenue.cc_revenue?.total)}</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-3 text-sm text-gray-900 pl-10">Cash Revenue</td>
                  <td className="px-6 py-3 text-sm text-right text-gray-900">{fmt(revenue.cash_revenue?.total)}</td>
                </tr>
                {revenue.everwash?.gross_revenue > 0 && (
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-3 text-sm text-gray-900 pl-10">EverWash Revenue</td>
                    <td className="px-6 py-3 text-sm text-right text-gray-900">{fmt(revenue.everwash.gross_revenue)}</td>
                  </tr>
                )}
                <tr className="bg-gray-50/50">
                  <td className="px-6 py-3 text-sm font-medium text-gray-900">Total Revenue</td>
                  <td className="px-6 py-3 text-sm text-right font-medium text-gray-900">
                    {fmt(revenue.total_revenue ?? ((revenue.cc_revenue?.total || 0) + (revenue.cash_revenue?.total || 0)))}
                  </td>
                </tr>
                {revenue.sales_tax > 0 && (
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-3 text-sm text-red-700 pl-10">Sales Tax ({revenue.tax_rate}%)</td>
                    <td className="px-6 py-3 text-sm text-right text-red-700">({fmt(revenue.sales_tax)})</td>
                  </tr>
                )}
                <tr className="bg-gray-50/50">
                  <td className="px-6 py-3 text-sm font-bold text-gray-900">Net Revenue</td>
                  <td className="px-6 py-3 text-sm text-right font-bold text-gray-900">{fmt(revenue.net_revenue)}</td>
                </tr>
                {revenue.expenses?.maintenance > 0 && (
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-3 text-sm text-red-700 pl-10">Maintenance</td>
                    <td className="px-6 py-3 text-sm text-right text-red-700">({fmt(revenue.expenses.maintenance)})</td>
                  </tr>
                )}
                {Object.entries(revenue.expenses?.utilities_by_category || {}).map(([cat, amount]) => (
                  <tr key={cat} className="hover:bg-gray-50">
                    <td className="px-6 py-3 text-sm text-red-700 pl-10">{categoryLabel(cat)}</td>
                    <td className="px-6 py-3 text-sm text-right text-red-700">({fmt(amount)})</td>
                  </tr>
                ))}
                {(() => {
                  const combined = revenue.combined_by_unit || {};
                  const ssRev = Object.entries(combined)
                    .filter(([k]) => k.startsWith('SS Bay'))
                    .reduce((sum, [, v]) => sum + (v?.total || 0), 0);
                  const autoRev = Object.entries(combined)
                    .filter(([k]) => k.includes('Auto Wash'))
                    .reduce((sum, [, v]) => sum + (v?.total || 0), 0);
                  const ssSoap = revenue.expenses?.soap_ss || 0;
                  const autoSoap = revenue.expenses?.soap_auto || 0;
                  const ssPct = ssRev > 0 ? ((ssSoap / ssRev) * 100).toFixed(1) : null;
                  const autoPct = autoRev > 0 ? ((autoSoap / autoRev) * 100).toFixed(1) : null;
                  return (
                    <>
                      {ssSoap > 0 && (
                        <tr className="hover:bg-gray-50">
                          <td className="px-6 py-3 text-sm text-red-700 pl-10">SS Soap/Chemicals (est.)</td>
                          <td className="px-6 py-3 text-sm text-right text-red-700">
                            ({fmt(ssSoap)})
                            {ssPct && <span className="text-xs text-gray-400 ml-1">{ssPct}% of rev</span>}
                          </td>
                        </tr>
                      )}
                      {autoSoap > 0 && (
                        <tr className="hover:bg-gray-50">
                          <td className="px-6 py-3 text-sm text-red-700 pl-10">Auto Soap/Chemicals (est.)</td>
                          <td className="px-6 py-3 text-sm text-right text-red-700">
                            ({fmt(autoSoap)})
                            {autoPct && <span className="text-xs text-gray-400 ml-1">{autoPct}% of rev</span>}
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })()}
                {revenue.expenses?.staff_labor > 0 && (
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-3 text-sm text-red-700 pl-10">Staff Labor</td>
                    <td className="px-6 py-3 text-sm text-right text-red-700">({fmt(revenue.expenses.staff_labor)})</td>
                  </tr>
                )}
                {revenue.expenses?.total > 0 && (
                  <tr className="bg-gray-50/50">
                    <td className="px-6 py-3 text-sm font-medium text-red-700">Total Expenses</td>
                    <td className="px-6 py-3 text-sm text-right font-medium text-red-700">({fmt(revenue.expenses.total)})</td>
                  </tr>
                )}
                <tr className="bg-gray-100 font-bold">
                  <td className="px-6 py-3 text-sm text-gray-900">Net Income</td>
                  <td className={`px-6 py-3 text-sm text-right ${revenue.net >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                    {revenue.net < 0 ? `(${fmt(Math.abs(revenue.net))})` : fmt(revenue.net)}
                  </td>
                </tr>
              </tbody>
            </table>
          </ScrollHint>
        </div>
      )}

      {/* P&L Over Time Table */}
      {revenue && revenue.revenue_over_time?.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">P&L Over Time</h2>
          </div>
          <ScrollHint className="max-h-[400px] overflow-y-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">CC Revenue</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Cash Revenue</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Revenue</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Expenses</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Daily Profit</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {revenue.revenue_over_time.map(row => {
                  const dailyProfit = (row.total_revenue || 0) - (row.expenses || 0);
                  return (
                    <tr key={row.date} className="hover:bg-gray-50">
                      <td className="px-6 py-3 text-sm text-gray-900">
                        {new Date(row.date + 'T12:00:00').toLocaleDateString()}
                      </td>
                      <td className="px-6 py-3 text-sm text-right text-gray-900">{fmt(row.cc_revenue)}</td>
                      <td className="px-6 py-3 text-sm text-right text-gray-900">{fmt(row.cash_revenue)}</td>
                      <td className="px-6 py-3 text-sm text-right font-bold text-gray-900">{fmt(row.total_revenue)}</td>
                      <td className="px-6 py-3 text-sm text-right text-red-600">{fmt(row.expenses)}</td>
                      <td className={`px-6 py-3 text-sm text-right font-bold ${dailyProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {dailyProfit < 0 ? `(${fmt(Math.abs(dailyProfit))})` : fmt(dailyProfit)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </ScrollHint>
        </div>
      )}

      {/* Sales Tax Calculator */}
      <SalesTaxCalculator />

      {/* Sales Tax Escrow — running tally of tax accrued vs transferred to escrow account */}
      <SalesTaxEscrow />

      {unmappedSwipers && (
        <UnmappedSwipersModal
          unmapped={unmappedSwipers}
          location={currentLocation}
          onCancel={() => setUnmappedSwipers(null)}
          onSaved={async () => {
            setUnmappedSwipers(null);
            // Backend already re-mapped historical rows; refresh revenue
            // so the dashboard reflects the newly-attributed transactions.
            await fetchRevenue();
          }}
        />
      )}

      {unmappedSites && (
        <UnmappedSitesModal
          unmappedSites={unmappedSites}
          locations={locations || []}
          onCancel={() => { setUnmappedSites(null); setPendingUploadFile(null); }}
          onSaved={async () => {
            setUnmappedSites(null);
            // Re-run the same file through the upload endpoint now that
            // the sites are mapped — this ingests the rows that were
            // previously skipped and may surface unmapped swipers next.
            const f = pendingUploadFile;
            setPendingUploadFile(null);
            if (f) await handleFileUpload(f);
          }}
        />
      )}

      {unmappedEverWash && (
        <UnmappedSitesModal
          unmappedSites={unmappedEverWash}
          locations={locations || []}
          idField="service_location_id"
          source="EverWash"
          onCancel={() => { setUnmappedEverWash(null); setPendingEverWashFile(null); }}
          onSaved={async () => {
            setUnmappedEverWash(null);
            const f = pendingEverWashFile;
            setPendingEverWashFile(null);
            if (f) await handleEverWashUpload(f);
          }}
        />
      )}

      {/* EverWash Upload Modal */}
      {showEverWashModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-black bg-opacity-40" onClick={() => setShowEverWashModal(false)} />
            <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full p-6 z-10">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Upload EverWash Income Report</h2>
                <button onClick={() => setShowEverWashModal(false)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
              </div>

              <div
                className="border-2 border-dashed border-gray-300 hover:border-gray-400 rounded-lg p-8 text-center cursor-pointer transition-colors"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); handleEverWashUpload(e.dataTransfer.files[0]); }}
                onClick={() => everwashInputRef.current?.click()}
              >
                <input
                  ref={everwashInputRef}
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={(e) => handleEverWashUpload(e.target.files[0])}
                />
                {everwashUploading ? (
                  <p className="text-gray-500">Uploading and processing...</p>
                ) : (
                  <>
                    <p className="text-gray-600 font-medium">Drop the monthly income report here or click to browse</p>
                    <p className="text-sm text-gray-400 mt-1">
                      Keep EverWash's filename (e.g. income-report_September-2026.csv) — that's where the month comes from
                    </p>
                  </>
                )}
              </div>

              {everwashError && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700">{everwashError}</p>
                </div>
              )}

              {everwashResult && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-green-800 font-medium">{everwashResult.message}</p>
                  <div className="mt-2 space-y-2">
                    {(everwashResult.per_location || []).map((p) => (
                      <div key={p.location_id} className="text-sm text-green-800">
                        <div className="font-medium">
                          {p.location_name || p.site_name}
                          {p.replaced && <span className="text-xs font-normal text-green-600"> (replaced existing)</span>}
                        </div>
                        <div className="text-xs text-green-700 grid grid-cols-2 gap-x-3">
                          <span>Revenue: ${p.total_revenue.toFixed(2)}</span>
                          <span>Sales tax: ${p.taxes.toFixed(2)}</span>
                          <span>CC fees: ${p.transaction_fees.toFixed(2)}</span>
                          <span>EverWash fees: ${p.everwash_fees_total.toFixed(2)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  {(everwashResult.unmapped_sites || []).length === 0 && (
                    <p className="text-xs text-green-700 mt-2">
                      Revenue charts as an "EverWash" unit; fees are on the P&amp;L under CC Processing and EverWash Fees.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


const SalesTaxEscrow = () => {
  const { currentLocation } = useLocationContext();
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [amount, setAmount] = useState('');
  const [paidOn, setPaidOn] = useState(() => new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const fmt = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLocation?.id]);

  const refresh = async () => {
    try {
      setLoading(true);
      const [s, h] = await Promise.all([getSalesTaxStatus(), listSalesTaxTransfers()]);
      setStatus(s);
      setHistory(h.transfers || []);
    } catch (err) {
      setError('Failed to load tax escrow data');
    } finally {
      setLoading(false);
    }
  };

  const handleFrequencyChange = async (freq) => {
    try {
      await setTaxFilingFrequency(freq);
      refresh();
    } catch {
      setError('Failed to update filing frequency');
    }
  };

  const handleSubmit = async () => {
    setError('');
    const amt = parseFloat(amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      setError('Enter an amount greater than zero');
      return;
    }
    try {
      setSaving(true);
      await createSalesTaxTransfer({ amount: amt, paid_on: paidOn, note });
      setAmount('');
      setNote('');
      setPaidOn(new Date().toISOString().slice(0, 10));
      setShowForm(false);
      refresh();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to record transfer');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this transfer from the record?')) return;
    try {
      await deleteSalesTaxTransfer(id);
      refresh();
    } catch {
      setError('Failed to remove transfer');
    }
  };

  if (loading) return null;
  if (!status) return null;

  const periodLabel = status.filing_frequency === 'quarterly'
    ? `Q${Math.floor((new Date(status.period_start + 'T12:00:00').getMonth()) / 3) + 1} ${new Date(status.period_start + 'T12:00:00').getFullYear()}`
    : new Date(status.period_start + 'T12:00:00').toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Sales Tax Escrow</h2>
          <p className="text-sm text-gray-500">
            Running balance for {periodLabel}
            {' '}({new Date(status.period_start + 'T12:00:00').toLocaleDateString()} – {new Date(status.period_end + 'T12:00:00').toLocaleDateString()})
          </p>
        </div>
        <label className="flex items-center gap-2 text-xs text-gray-600">
          Filing period
          <select
            value={status.filing_frequency}
            onChange={(e) => handleFrequencyChange(e.target.value)}
            className="border border-gray-300 rounded px-2 py-1 text-sm"
          >
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
          </select>
        </label>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
        <div className="border border-gray-200 rounded-lg p-3">
          <div className="text-xs uppercase tracking-wide text-gray-500">Accrued this period</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{fmt(status.accrued_tax)}</div>
          <div className="text-xs text-gray-400 mt-1">on {fmt(status.total_revenue)} revenue</div>
        </div>
        <div className="border border-gray-200 rounded-lg p-3">
          <div className="text-xs uppercase tracking-wide text-gray-500">Already transferred</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{fmt(status.transferred_amount)}</div>
          <div className="text-xs text-gray-400 mt-1">
            {status.last_transfer
              ? `Last: ${fmt(status.last_transfer.amount)} on ${new Date(status.last_transfer.paid_on + 'T12:00:00').toLocaleDateString()}`
              : 'No transfers yet'}
          </div>
        </div>
        <div className={`border rounded-lg p-3 ${status.amount_owed > 0 ? 'border-amber-300 bg-amber-50' : 'border-green-200 bg-green-50'}`}>
          <div className="text-xs uppercase tracking-wide text-gray-500">Currently owed</div>
          <div className={`text-2xl font-bold mt-1 ${status.amount_owed > 0 ? 'text-amber-700' : 'text-green-700'}`}>
            {fmt(status.amount_owed)}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {status.amount_owed > 0 ? 'Move to your tax-escrow account' : 'You\'re caught up'}
          </div>
        </div>
      </div>

      {!showForm ? (
        <button
          onClick={() => setShowForm(true)}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium"
        >
          Mark transfer as paid
        </button>
      ) : (
        <div className="border border-gray-200 rounded-lg p-4 mb-2 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <label className="block">
              <span className="block text-xs text-gray-600 mb-1">Amount transferred</span>
              <input
                type="text"
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                autoFocus
              />
            </label>
            <label className="block">
              <span className="block text-xs text-gray-600 mb-1">Date</span>
              <input
                type="date"
                value={paidOn}
                onChange={(e) => setPaidOn(e.target.value)}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              />
            </label>
            <label className="block">
              <span className="block text-xs text-gray-600 mb-1">Note (optional)</span>
              <input
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="confirmation #, etc."
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
              />
            </label>
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex gap-2">
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md text-sm font-medium disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save transfer'}
            </button>
            <button
              onClick={() => { setShowForm(false); setError(''); }}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {history.length > 0 && (
        <details className="mt-4">
          <summary className="cursor-pointer text-sm font-medium text-gray-700">
            Transfer history ({history.length})
          </summary>
          <div className="mt-2 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Paid on</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">For period</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Note</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {history.map(t => (
                  <tr key={t.id}>
                    <td className="px-3 py-2 text-gray-700">{new Date(t.paid_on + 'T12:00:00').toLocaleDateString()}</td>
                    <td className="px-3 py-2 text-right text-gray-900">{fmt(t.amount)}</td>
                    <td className="px-3 py-2 text-gray-700">
                      {new Date(t.period_start + 'T12:00:00').toLocaleDateString()} – {new Date(t.period_end + 'T12:00:00').toLocaleDateString()}
                    </td>
                    <td className="px-3 py-2 text-gray-500">{t.note || '—'}</td>
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={() => handleDelete(t.id)}
                        className="text-red-600 hover:text-red-800 text-xs"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </div>
  );
};


export default PLDashboard;
